// "Việc" = một issue nhìn qua lăng kính giao diện. Mọi trường DẪN RA từ nhãn/body/D1 — không lưu riêng.
import type { IssueGh } from './kieu';
import { khoangKy, nguoiLucDong, type LanDoiPhuTrach, type ViecThongKe } from '../../server/chi-so';
import { cacMucChecklist, doiMucChecklist } from '../../server/muc-checklist';
import { boDau, duoi } from './nhan';

export type TrangThai = 'tt:inbox' | 'tt:san-sang' | 'tt:dang-chay' | 'tt:cho-duyet' | 'tt:tac-nghen' | 'xong';

export const TRANG_THAI: { ma: TrangThai; ten: string; mau: string }[] = [
  { ma: 'tt:inbox', ten: 'Inbox', mau: 'var(--tt-inbox)' },
  { ma: 'tt:san-sang', ten: 'Sẵn sàng', mau: 'var(--tt-san-sang)' },
  { ma: 'tt:dang-chay', ten: 'Đang làm', mau: 'var(--tt-dang-chay)' },
  { ma: 'tt:cho-duyet', ten: 'Chờ duyệt', mau: 'var(--tt-cho-duyet)' },
  { ma: 'tt:tac-nghen', ten: 'Tắc nghẽn', mau: 'var(--tt-tac-nghen)' },
  { ma: 'xong', ten: 'Xong', mau: 'var(--tt-xong)' },
];
/** Tên việc AI đặt thường mở đầu bằng "[brand] " — khi brand đã hiện ở chỗ khác (chip, cột, thẻ) thì bỏ tiền tố để tên đỡ bị cắt.
 *  Chỉ bỏ khi chữ trong ngoặc KHỚP brand của việc (không dấu, bỏ khoảng trắng và dấu chấm: "[G.Organic]" ~ brand:gorganic);
 *  "[Ngoại Làm · Núi Rừng]", "[VTask]"… giữ nguyên. Bỏ xong mà rỗng thì giữ tên gốc. */
export function boTienToBrand(tieu_de: string, brand: string | null): string {
  if (!brand) return tieu_de;
  const m = tieu_de.match(/^\[([^\]]{1,40})\]\s*/);
  if (!m) return tieu_de;
  const chuan = (s: string) => boDau(s).replace(/[^a-z0-9]/g, '');
  const con_lai = tieu_de.slice(m[0].length);
  return con_lai && chuan(m[1]) === chuan(brand.replace(/^brand:/, '')) ? con_lai : tieu_de;
}

/** Loại việc cho ô lọc (mục tiêu không có: danh sách việc vốn đã loại mục tiêu — chúng có trang OKR riêng). */
export const LOAI_VIEC = [
  { ma: 'loai:viec', ten: 'Việc' },
  { ma: 'loai:quyet-dinh', ten: 'Quyết định' },
  { ma: 'loai:loi', ten: 'Lỗi' },
] as const;
/** Trạng thái đang mở (ô lọc "Trạng thái": việc đã xong không nằm trong danh sách mặc định). */
export const TT_DANG_MO = TRANG_THAI.filter((t) => t.ma !== 'xong').map((t) => t.ma as string);

export const thongTinTt = (ma: TrangThai) => TRANG_THAI.find((t) => t.ma === ma) ?? TRANG_THAI[0];

export const UU_TIEN = [
  { ma: 'uu-tien:p0', ten: 'Khẩn', ngan: 'P0' },
  { ma: 'uu-tien:p1', ten: 'Cao', ngan: 'P1' },
  { ma: 'uu-tien:p2', ten: 'Vừa', ngan: 'P2' },
  { ma: 'uu-tien:p3', ten: 'Thấp', ngan: 'P3' },
] as const;
export const thuTuUuTien = (ma: string | null) => {
  const i = UU_TIEN.findIndex((u) => u.ma === ma);
  return i < 0 ? 9 : i;
};

export interface Viec {
  so: number;
  tieu_de: string;
  than: string;
  mo: boolean;
  tt: TrangThai;
  uu_tien: string | null;
  loai: string | null;
  che_do: string | null;
  agent: string | null;
  linh_vuc: string[];
  brand: string | null;
  tags: string[];
  doi_tac: string[];  // nhãn doi-tac:<slug>
  tai_san: string[];  // nhãn tai-san:<slug> — tài sản thương hiệu (trang Tài sản, bảng D1 tai_san)
  pha: string[];      // nhãn pha:p0…p5 — pha hành vi khách hàng (sơ đồ pha × kênh, lib/pha-kenh.ts)
  kenh: string[];     // nhãn kenh:<slug> — kênh triển khai
  ky: string | null;  // chu kỳ OKR: nhãn ky:<năm>-q<n> → '2026-q4'
  han: string | null; // YYYY-MM-DD
  bat_dau: string | null; // YYYY-MM-DD — mục "### Bắt đầu" (timeline); không có thì timeline dùng ngày tạo
  tien_do: { xong: number; tong: number } | null;
  con?: { xong: number; tong: number } | null; // việc con (sub-issue) — riêng với checklist, để biết có việc con hay không
  nguoi: number[];
  so_binh_luan: number;
  tao_luc: string;
  cap_nhat_luc: string;
  dong_luc: string | null;
  url: string;
  nhan: string[];
}

// Mục ngày trong body: "### Hạn" (issue form + v1) và "### Bắt đầu" (timeline) — cùng một quy ước.
export type MucNgay = 'Hạn' | 'Bắt đầu';

const reNgay = (muc: MucNgay) => new RegExp(String.raw`###\s*${muc}\s*\n+\s*(\d{4}-\d{2}-\d{2})`);
const reMuc = (muc: MucNgay) => new RegExp(String.raw`###\s*${muc}\s*\n+\s*(?:\d{4}-\d{2}-\d{2}|_No response_)?[ \t]*(?:\n|$)`);

export function timNgay(than: string | null, muc: MucNgay): string | null {
  const m = (than || '').match(reNgay(muc));
  return m ? m[1] : null;
}
export const timHan = (than: string | null) => timNgay(than, 'Hạn');

// Thay (hoặc thêm / gỡ) một mục ngày trong body.
export function datNgay(than: string | null, muc: MucNgay, gia_tri: string | null): string {
  const goc = than || '';
  const re = reMuc(muc);
  if (re.test(goc)) {
    return gia_tri ? goc.replace(re, `### ${muc}\n\n${gia_tri}\n`).trimEnd() : goc.replace(re, '').trimEnd();
  }
  if (!gia_tri) return goc;
  return `${goc.trimEnd()}${goc.trim() ? '\n\n' : ''}### ${muc}\n\n${gia_tri}`;
}
export const datHan = (than: string | null, han: string | null) => datNgay(than, 'Hạn', han);

// Mục checklist trong mô tả (task list của GFM): logic thuần ở server/muc-checklist.ts (dùng chung với server — đánh dấu bước
// trong mô tả nội bộ). Giữ tên cũ ở đây để nơi gọi không phải đổi.
export { cacMucChecklist, doiMucChecklist };

export function tienDoChecklist(than: string): { xong: number; tong: number } | null {
  const muc = cacMucChecklist(than);
  if (!muc.length) return null;
  return { xong: muc.filter((m) => m.xong).length, tong: muc.length };
}

export function tuIssue(i: IssueGh, phu_trach: Record<string, number[]>): Viec {
  const nhan = i.labels.map((l) => l.name);
  const mot = (tien_to: string) => nhan.find((n) => n.startsWith(tien_to)) ?? null;
  const tt = (i.state === 'closed' ? 'xong' : mot('tt:') ?? 'tt:inbox') as TrangThai;
  const con = i.sub_issues_summary;
  return {
    so: i.number,
    tieu_de: i.title,
    than: i.body || '',
    mo: i.state === 'open',
    tt,
    uu_tien: mot('uu-tien:'),
    loai: mot('loai:'),
    che_do: mot('che-do:'),
    agent: mot('agent:'),
    linh_vuc: nhan.filter((n) => n.startsWith('linh-vuc:')),
    brand: mot('brand:'),
    tags: nhan.filter((n) => n.startsWith('tag:')).map(duoi),
    doi_tac: nhan.filter((n) => n.startsWith('doi-tac:')).map(duoi),
    tai_san: nhan.filter((n) => n.startsWith('tai-san:')).map(duoi),
    pha: nhan.filter((n) => n.startsWith('pha:')).map(duoi),
    kenh: nhan.filter((n) => n.startsWith('kenh:')).map(duoi),
    ky: nhan.find((n) => /^ky:\d{4}-q[1-4]$/.test(n))?.slice(3) ?? null,
    han: timHan(i.body),
    bat_dau: timNgay(i.body, 'Bắt đầu'),
    tien_do: con && con.total > 0 ? { xong: con.completed, tong: con.total } : tienDoChecklist(i.body || ''),
    con: con && con.total > 0 ? { xong: con.completed, tong: con.total } : null,
    nguoi: phu_trach[i.number] || [],
    so_binh_luan: i.comments,
    tao_luc: i.created_at,
    cap_nhat_luc: i.updated_at,
    dong_luc: i.closed_at,
    url: i.html_url,
    nhan,
  };
}

export const homNay = () => new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD theo giờ máy

export const treHan = (v: Viec) => v.mo && !!v.han && v.han < homNay();

// Bộ lọc hạn trên URL (?han=…). `tre` dùng ĐÚNG luật treHan (đang mở + hạn đã qua) — ô "Trễ hạn" ở Tổng quan đếm bằng
// treHan và dẫn tới ?han=tre, hai con số phải bằng nhau. Các mức còn lại chỉ nhìn ngày; việc đã đóng thì danh sách tự ẩn.
export type LocHan = 'tre' | 'hom-nay' | '7-ngay' | 'khong-han';
export const LOC_HAN: { ma: LocHan; ten: string }[] = [
  { ma: 'tre', ten: 'Trễ hạn' },
  { ma: 'hom-nay', ten: 'Hôm nay' },
  { ma: '7-ngay', ten: '7 ngày tới' },
  { ma: 'khong-han', ten: 'Chưa có hạn' },
];
export const laLocHan = (x: string | null): x is LocHan => LOC_HAN.some((h) => h.ma === x);

const congNgay = (ngay: string, so: number) => new Date(Date.parse(ngay + 'T00:00:00Z') + so * 86400_000).toISOString().slice(0, 10);

export function khopHan(v: Viec, loc: LocHan, hom_nay = homNay()): boolean {
  if (loc === 'khong-han') return !v.han;
  if (!v.han) return false;
  if (loc === 'tre') return v.mo && v.han < hom_nay;
  if (loc === 'hom-nay') return v.han === hom_nay;
  return v.han >= hom_nay && v.han <= congNgay(hom_nay, 7);
}

// "Xong 7 ngày qua" — một luật cho cả ô đếm ở Tổng quan lẫn danh sách ?xong=7-ngay.
export const xongTrong7Ngay = (v: Viec, bay_gio = Date.now()) => !v.mo && !!v.dong_luc && +new Date(v.dong_luc) > bay_gio - 7 * 86400_000;

// Kỳ THÁNG LỊCH theo giờ máy người xem — cùng cách chia kỳ với server/chi-so.ts (khoangKy), để con số ở Báo cáo kỳ và
// danh sách nó mở ra (?xong=YYYY-MM, ?tao=YYYY-MM) là MỘT luật. Chỉ nhận tháng: "tuần" đang có ba cách tính khác nhau
// trong app, không đưa lên URL.
export const laKyThang = (x: string | null | undefined): x is string => typeof x === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(x);

function trongKy(iso: string | null, ky: string): boolean {
  const k = laKyThang(ky) ? khoangKy(ky) : null;
  if (!k || !iso) return false;
  const t = +new Date(iso);
  return t >= +k[0] && t < +k[1];
}
/** "Xong trong kỳ" (?xong=YYYY-MM): việc ĐÃ ĐÓNG có ngày đóng trong tháng đó. */
export const xongTrongKy = (v: Viec, ky: string) => !v.mo && trongKy(v.dong_luc, ky);
/** "Mở mới trong kỳ" (?tao=YYYY-MM): việc có ngày tạo trong tháng đó, dù còn mở hay đã đóng. */
export const taoTrongKy = (v: Viec, ky: string) => trongKy(v.tao_luc, ky);

// "Cần duyệt" — một luật cho cả trang /can-duyet lẫn ô đếm ở Tổng quan.
export const canDuyet = (v: Viec) => v.mo && (v.tt === 'tt:cho-duyet' || v.tt === 'tt:tac-nghen' || v.loai === 'loai:quyet-dinh');

// Tag hay dùng: `so_luong` tag (hậu tố, không "tag:") gặp nhiều nhất trên các việc đã tải; hoà thì theo ABC cho ổn định.
export function tagHayDung(ds: Viec[], so_luong = 8): string[] {
  const dem = new Map<string, number>();
  for (const v of ds) for (const t of v.tags) dem.set(t, (dem.get(t) || 0) + 1);
  return [...dem].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, so_luong).map(([t]) => t);
}

// Trạng thái một tag trên nhóm việc đang chọn (sửa hàng loạt): tất cả có / vài việc có / không việc nào.
export function trangThaiTagNhom(ds: Viec[], tag: string): 'tat-ca' | 'mot-phan' | 'khong' {
  const co = ds.filter((v) => v.tags.includes(tag)).length;
  return co === 0 ? 'khong' : co === ds.length ? 'tat-ca' : 'mot-phan';
}

export function phanTram(v: Viec): number | null {
  if (!v.mo) return 100;
  return v.tien_do ? Math.round((v.tien_do.xong / v.tien_do.tong) * 100) : null;
}

// Thay nhãn của MỘT trục (tt:, uu-tien:...) — giữ nguyên mọi nhãn khác.
export function thayTruc(nhan: string[], tien_to: string, moi: string | null): string[] {
  const giu = nhan.filter((n) => !n.startsWith(tien_to));
  return moi ? [...giu, moi] : giu;
}

export const laMucTieu = (v: Viec) => v.loai === 'loai:muc-tieu';

// 🤖 AI làm: bộ nhãn để executor (`drk vtask`, UseAI keywords/vtask.md) nhận việc. Executor chỉ lấy
// `loai:viec` + `tt:san-sang`, bỏ `ai:dung-cham` và `che-do:thu-cong` — nên nút phải đưa việc về đúng
// dạng đó, không thì bấm xong việc vẫn nằm im. Trả `loi` khi không được giao cho AI.
export function nhanChoAi(nhan: string[]): { nhan: string[] } | { loi: string } {
  if (nhan.includes('ai:dung-cham')) return { loi: 'Việc đang gắn ai:dung-cham — gỡ nhãn đó trước khi giao AI.' };
  if (nhan.includes('loai:muc-tieu')) return { loi: 'Mục tiêu không giao AI trực tiếp — giao từng việc con.' };
  let moi = thayTruc(nhan, 'tt:', 'tt:san-sang');
  if (moi.includes('che-do:thu-cong')) moi = thayTruc(moi, 'che-do:', 'che-do:duyet');
  if (!moi.some((n) => n.startsWith('agent:'))) moi = [...moi, 'agent:chung'];
  return { nhan: moi };
}

// Việc → dạng đầu vào của công thức năng suất (server/chi-so.ts).
export const thongKe = (v: Viec): ViecThongKe => ({
  so: v.so, mo: v.mo, tao_luc: v.tao_luc, dong_luc: v.dong_luc, han: v.han, nguoi: v.nguoi,
  doi_tac: v.doi_tac, brand: v.brand, tags: v.tags, la_muc_tieu: laMucTieu(v),
});

// `since` cho lịch sử việc đóng, làm tròn về 00:00 UTC: URL đứng yên cả ngày → trúng cache dùng chung của server và
// ETag của trình duyệt. Để nguyên mili-giây thì mỗi lượt một URL, yêu cầu nặng nhất không bao giờ trúng cache (đo thật
// 28/09). Lấy dư tối đa một ngày; bộ lọc hiển thị (useViecDaDong) vẫn cắt đúng cửa sổ.
export const sinceTronNgay = (so_ngay: number, bay_gio = Date.now()) =>
  new Date(bay_gio - so_ngay * 86400_000).toISOString().slice(0, 10) + 'T00:00:00Z';

// Cho thống kê: việc ĐÃ ĐÓNG mang người phụ trách LÚC ĐÓNG thay cho người hiện tại (server/chi-so.ts nguoiLucDong).
// Chỉ áp cho danh sách đưa vào công thức; ngăn chi tiết vẫn hiện (và sửa) người phụ trách hiện tại.
export function ganNguoiLucDong(ds: Viec[], doi: Record<string, LanDoiPhuTrach[]> | undefined): Viec[] {
  if (!doi) return ds;
  return ds.map((v) => (v.mo || !doi[v.so] ? v : { ...v, nguoi: nguoiLucDong(v.dong_luc, v.nguoi, doi[v.so]) }));
}

// Tìm việc: không dấu, không phân biệt hoa thường ("khao sat" khớp "Khảo sát"). Hai cách khớp, một trong hai là đủ:
//   1. NGUYÊN CỤM như trước — tiêu đề, #số, tag; từ 3 ký tự thì cả trong MÔ TẢ.
//   2. TỪNG TỪ (owner 30/09: "tìm kiếm chưa hoạt động" — gõ "logo gorganic" ra rỗng vì chỉ khớp cụm liền nhau): mọi
//      từ đều phải có mặt ở đâu đó trong tiêu đề, #số, tag, brand, đối tác, lĩnh vực, tài sản, kênh, tên người phụ
//      trách; từ dài từ 3 ký tự được tìm cả trong mô tả.
// Chuỗi bỏ dấu được nhớ theo từng việc — gõ tới đâu lọc tới đó mà không phải bỏ dấu lại vài trăm việc mỗi phím.
const mo_ta_khong_dau = new WeakMap<Viec, string>();
const nhan_khong_dau = new WeakMap<Viec, string>();
const moTaKhongDau = (v: Viec) => {
  let md = mo_ta_khong_dau.get(v);
  if (md === undefined) { md = boDau(v.than || ''); mo_ta_khong_dau.set(v, md); }
  return md;
};
const nhanKhongDau = (v: Viec) => {
  let c = nhan_khong_dau.get(v);
  if (c === undefined) {
    c = boDau([v.tieu_de, ...(v.tags || []), v.brand || '', ...(v.doi_tac || []), ...(v.linh_vuc || []), ...(v.tai_san || []), ...(v.kenh || [])].join(' '));
    nhan_khong_dau.set(v, c);
  }
  return c;
};
// Tên người phụ trách để tìm theo người ("lan" ra việc giao cho Lan). Khung ứng dụng nạp danh sách người rồi gọi hàm này;
// chưa nạp thì chỉ thiếu phần tìm theo tên, không lỗi.
let ten_nguoi_tim = new Map<number, string>();
export function ghiTenNguoiTim(ds: { id: number; ten: string }[]) {
  ten_nguoi_tim = new Map(ds.map((n) => [n.id, boDau(n.ten)]));
}
// Tách ô tìm thành từ (đã bỏ dấu); bỏ "từ" chỉ có dấu câu như "+", "-".
export const tachTuTim = (tim: string) => [...new Set(boDau(tim).split(/\s+/).filter((t) => /[a-z0-9]/.test(t)))];
const khopSo = (v: Viec, t: string) => /^#?\d+$/.test(t) && String(v.so) === t.replace(/^#/, '');
// Từ ngắn (< 3 ký tự) phải khớp NGUYÊN TỪ ("q4" khớp tag q4, "an" không khớp giữa "thanh"); từ dài khớp một phần.
const coTu = (chu: string, t: string) =>
  t.length >= 3 ? chu.includes(t) : new RegExp('(^|[^a-z0-9])' + t.replace(/[^a-z0-9]/g, '\\$&') + '([^a-z0-9]|$)').test(chu);
const tenNguoiCua = (v: Viec) => (v.nguoi || []).map((id) => ten_nguoi_tim.get(id) || '').join(' ');
// Khớp mà KHÔNG cần mô tả (bảng lệnh xếp nhóm này lên trước). `t` đã bỏ dấu.
export function khopTieuDe(v: Viec, t: string): boolean {
  if (khopSo(v, t) || boDau(v.tieu_de).includes(t) || v.tags.some((g) => g.includes(t))) return true;
  const tu = tachTuTim(t);
  if (!tu.length) return false;
  const chu = nhanKhongDau(v);
  const ten = tenNguoiCua(v);
  return tu.every((x) => khopSo(v, x) || coTu(chu, x) || coTu(ten, x));
}
export function khopTim(v: Viec, tim: string): boolean {
  const t = boDau(tim.trim());
  if (!t || khopTieuDe(v, t)) return true;
  if (t.length >= 3 && moTaKhongDau(v).includes(t)) return true;
  const tu = tachTuTim(t);
  if (!tu.length) return false;
  const chu = nhanKhongDau(v);
  const ten = tenNguoiCua(v);
  return tu.every((x) => khopSo(v, x) || coTu(chu, x) || coTu(ten, x) || (x.length >= 3 && moTaKhongDau(v).includes(x)));
}
